// @ts-strict-ignore
import { ClickAwayListener } from '@material-ui/core';
import type { PopperPlacementType } from '@material-ui/core/Popper';
import Popper from '@material-ui/core/Popper';
import type { FilterOptionsState, Value } from '@material-ui/lab';
import type {
  AutocompleteCloseReason,
  AutocompleteGetTagProps,
  AutocompleteRenderOptionState,
} from '@material-ui/lab/Autocomplete';
import * as React from 'react';
import FocusLock from 'react-focus-lock';

import { Divide } from '@/atoms/Divide';
import { Button } from '@/components/Button/Button';
import { NeedlAutocomplete } from '@/components/NeedlAutocomplete';

export const NeedlAutocompletePopper = <T, Multiple extends boolean = true>({
  anchorEl,
  filterOptions,
  forceMount,
  getOptionLabel,
  id,
  noOptionsText,
  onChange,
  onClose,
  open,
  clearOnBlur = true,
  options,
  placement = 'bottom-start',
  primaryButtonText,
  portal = true,
  renderOption,
  value,
  className,
  disabled = false,
  multiple,
  placeholder,
  endAdornment,
  renderTags = () => null,
  primaryButtonHandler,
}: NeedlAutocompletePopperProps<T, Multiple>) => {
  const closeHandler = primaryButtonText ? () => null : onClose;

  React.useEffect(() => {
    if (primaryButtonText) {
      const eventListener = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose(e, 'blur');
        }
      };

      window.addEventListener('keyup', eventListener, false);

      return () => {
        window.removeEventListener('keyup', eventListener);
      };
    }
  }, [onClose, primaryButtonText]);

  if (!import.meta.env.PROD) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      if (forceMount && portal && !anchorEl) {
        throw new Error(
          'CreatableAutocomplete: forceMount is true but anchorEl is not provided.'
        );
      }
    }, [anchorEl, forceMount, portal]);
  }

  const autocomplete = (
    <Wrapper
      anchorEl={anchorEl}
      className={className}
      id={id}
      open={forceMount || open}
      placement={placement}
      portal={portal}
    >
      <NeedlAutocomplete<T, Multiple>
        open
        onClose={closeHandler}
        multiple={multiple}
        disablePortal
        disableCloseOnSelect
        handleHomeEndKeys
        clearOnBlur={clearOnBlur}
        options={options}
        value={value}
        onChange={onChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderTags={renderTags}
        placeholder={placeholder}
        filterOptions={filterOptions}
        endAdornment={endAdornment}
        noOptionsText={noOptionsText}
        getOptionDisabled={() => disabled}
        isPopperOrSubMenu
      />
      {primaryButtonText ? (
        <>
          <Divide />
          <Button
            className='ml-auto my-2 mr-4'
            variant='primary'
            size='medium'
            id='autocomplete-primary-button'
            label={primaryButtonText}
            onClick={primaryButtonHandler}
          />
        </>
      ) : null}
    </Wrapper>
  );

  if (primaryButtonText) {
    return (
      <ClickAwayListener
        mouseEvent='onMouseDown'
        touchEvent='onTouchStart'
        onClickAway={(e) => {
          if (open) {
            onClose(e, 'blur');
          }
        }}
      >
        {autocomplete}
      </ClickAwayListener>
    );
  }

  return <>{autocomplete}</>;
};

const Wrapper = React.forwardRef<HTMLDivElement, WrapperProps>(
  ({ anchorEl, children, className, id, open, placement, portal }, ref) => {
    return portal ? (
      <Popper
        ref={ref}
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        className={`rounded w-[var(--radix-dropdown-menu-content-available-width,18rem)] sm:w-72 shadow-common-card bg-white ${className?.popper}`}
      >
        <FocusLock returnFocus>{children}</FocusLock>
      </Popper>
    ) : (
      <div
        ref={ref}
        className='relative w-[var(--radix-dropdown-menu-content-available-width,18rem)] sm:w-72'
      >
        {children}
      </div>
    );
  }
);

const usePopperState = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleOpen = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (e.defaultPrevented) {
        return;
      }
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    },
    []
  );

  const handleClose = React.useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      reason: AutocompleteCloseReason
    ) => {
      if (e.defaultPrevented) {
        return;
      }
      if (reason === 'toggleInput') {
        return;
      }
      if (anchorEl) {
        anchorEl.focus();
      }
      setAnchorEl(null);
    },
    [anchorEl]
  );

  const open = Boolean(anchorEl);

  return {
    anchorEl,
    open,
    handleOpen,
    handleClose,
  };
};

NeedlAutocompletePopper.usePopperState = usePopperState;

type NeedlAutocompletePopperProps<T, Multiple extends boolean> = {
  id?: string;
  open: boolean;
  clearOnBlur?: boolean;
  anchorEl: HTMLElement | null;
  placement?: PopperPlacementType;
  className?: {
    popper?: string;
  };
  onClose: (e: unknown, reason: AutocompleteCloseReason) => void;
  options: T[];
  value: Value<T, Multiple, undefined, undefined>;
  onChange: (
    e: React.ChangeEvent<unknown>,
    value: Value<T, Multiple, undefined, undefined>
  ) => void;
  getOptionLabel: (option: T) => string;
  filterOptions: (options: T[], state: FilterOptionsState<T>) => T[];
  forceMount?: boolean;
  renderOption: (
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  disabled?: boolean;
  noOptionsText: string;
  multiple: Multiple;
  placeholder: string;
  portal?: boolean;
  endAdornment?: React.ReactNode;
  renderTags?: (
    value: T[],
    getTagProps: AutocompleteGetTagProps
  ) => React.ReactNode;
} & (
  | {
      primaryButtonText: string;
      primaryButtonHandler: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
      ) => void;
    }
  | {
      primaryButtonText?: undefined;
      primaryButtonHandler?: undefined;
    }
);

type WrapperProps = {
  anchorEl: HTMLElement | (() => HTMLElement);
  children: React.ReactNode;
  className?: {
    popper?: string;
  };
  id: string;
  open: boolean;
  placement: PopperPlacementType;
  portal: boolean;
};
